/**
 * Declarative config of the shared documents card (`DocumentsByCategoryCard`):
 * the document view model, its value descriptors (stable keys `nom`, `date`)
 * and the category grouping.
 */

import type { InfoItemDescriptor } from '@components/shared/configurable';

/** One document, mapped by the domain service from a `WS2_EA_TAB_DOCS_*` row. */
export interface DocumentItem {
  /** Stable row id (`ID_ADOC`). */
  id: string;
  /** Category label (`LIB_DS`); `null` ⇒ unlabelled group. */
  categorie: string | null;
  /** Document name (`LIB_ADOC`). */
  nom: string | null;
  /** Document date, pre-formatted `JJ/MM/AAAA` (`DAT_ADOC`). */
  date: string | null;
}

/** Stable keys of a document's displayed values. */
export type DocumentItemKey = 'nom' | 'date';

/** A document value descriptor (override-able surface + display flag). */
export interface DocumentItemField extends InfoItemDescriptor {
  key: DocumentItemKey;
  /** Prefix the value with `label` (`Label : value`). */
  showLabel?: boolean;
}

/** Base descriptors: name first (primary line), then the date. */
export const DOCUMENT_ITEM_FIELDS: readonly DocumentItemField[] = [
  { key: 'nom', label: 'Nom du document', order: 0 },
  { key: 'date', label: 'Date', order: 1 },
];

/** A category and its documents, in API order. */
export interface DocumentCategoryGroup {
  /** Stable group key (the category label, `''` for `null`). */
  key: string;
  label: string;
  documents: DocumentItem[];
}

/**
 * Group documents by `categorie`, preserving the API order: categories in
 * the order of their first document, documents in API order inside.
 */
export function groupDocumentsByCategory(
  documents: readonly DocumentItem[],
): DocumentCategoryGroup[] {
  const groups = new Map<string, DocumentCategoryGroup>();
  for (const document of documents) {
    const label = document.categorie?.trim() ?? '';
    let group = groups.get(label);
    if (!group) {
      group = { key: label, label, documents: [] };
      groups.set(label, group);
    }
    group.documents.push(document);
  }
  return [...groups.values()];
}

/** The display text of a descriptor for a document. */
export function documentFieldText(field: DocumentItemField, document: DocumentItem): string {
  const value = document[field.key]?.trim() ?? '';
  if (value === '' || !field.showLabel || !field.label) return value;
  return `${field.label} : ${value}`;
}
